import { useEffect } from 'react';
import { useSelector, connect, useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';

// routing
import Routes from 'routes';

// defaultTheme
import themes from 'themes';

// project imports
import NavigationScroll from 'layout/NavigationScroll';
import useLogout from 'hooks/useLogout';
import { set_auth_token, set_logged_in } from 'store/actions';
import { MENU_OPEN } from 'store/action-types';

const mapDispatchToProps = (dispatch) => {
  return {
    setAuthToken: (data) => dispatch(set_auth_token(data)),
    setLoggedIn: (data) => dispatch(set_logged_in(data))
  };
};

const ids = {
  '/': 'default',
  '/infrastructure': 'default',
  '/locations': 'locations',
  '/turbines': 'turbines',
  '/forecasts': 'forecasts',
  '/sources': 'sources',
  '/comparison': 'comparison',
  '/validator-backtests': 'validator-backtests',
  '/variables': 'variables',
  '/metrics': 'metrics',
  '/unavailabilities': 'unavailabilities'
};

// ==============================|| APP ||============================== //

const App = ({ setAuthToken, setLoggedIn }) => {
  const customization = useSelector((state) => state.customization);
  const location = useLocation();
  const dispatch = useDispatch();
  const auth = useAuth();
  const { logout } = useLogout();

  useEffect(() => {
    const id = ids[`/${location.pathname.split('/')[1]}`];
    if (id) {
      dispatch({ type: MENU_OPEN, id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    if (auth.isAuthenticated && auth.user?.access_token) {
      // Mirrored into localStorage so loginReducer's synchronous initial
      // state (read on page load, before oidc-client-ts finishes restoring
      // its own session) still has a token to rehydrate from immediately.
      localStorage.setItem('token', auth.user.access_token);
      localStorage.setItem('isLoggedIn', true);
      setAuthToken(auth.user.access_token);
      setLoggedIn(true);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('isLoggedIn');
      setLoggedIn(false);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user, setAuthToken, setLoggedIn]);

  useEffect(() => {
    // automaticSilentRenew (oidcConfig.js) exchanges the stored refresh_token
    // for a new access_token before expiry and retries on timeout, but gives
    // up and raises this event once the refresh_token itself is no longer
    // valid (Keycloak SSO session ended, grant revoked). Left unhandled, the
    // app keeps the stale access_token until it actually expires, silently
    // failing every API call in between instead of sending the user to login.
    const handleSilentRenewError = (error) => {
      console.error('Silent token renewal failed, signing out', error);
      logout();
    };

    return auth.events.addSilentRenewError(handleSilentRenewError);
  }, [auth.events, logout]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <NavigationScroll>
          <Routes />
        </NavigationScroll>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

App.propTypes = {
  setAuthToken: PropTypes.func,
  setLoggedIn: PropTypes.func
};

export default connect(null, mapDispatchToProps)(App);
